import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Popconfirm,
  message,
  Card,
  Row,
  Col,
  Input,
  Tag,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SettingOutlined,
  SearchOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { attributeService } from '../../services';

const AttributeList = () => {
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const navigate = useNavigate();

  useEffect(() => {
    loadAttributes();
  }, []);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadAttributes();
  }, []);

  const loadAttributes = async () => {
    try {
      setLoading(true);
      const response = await attributeService.getAll();
      if (response.success) {
        setAttributes(response.data || []);
      } else {
        message.error('Không thể tải danh sách thuộc tính');
      }
    } catch (error) {
      //console.error('Error loading attributes:', error);
      message.error('Lỗi khi tải danh sách thuộc tính');
    } finally {
      setLoading(false);
    }
  };

  // 📱 Mobile Attribute Card Component
  const MobileAttributeCard = ({ attribute }) => {
    const valueCount = attribute.values ? attribute.values.length : 0;
    
    return (
      <Card
        size="small"
        style={{
          marginBottom: 12,
          border: '1px solid #d9d9d9',
          borderRadius: 8,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}
        bodyStyle={{ padding: 12 }}
      >
        <div>
          <div style={{ 
            fontWeight: 'bold', 
            fontSize: 14, 
            marginBottom: 4,
            color: '#333'
          }}>
            {attribute.name}
          </div>
          
          <div style={{ 
            fontSize: 12, 
            color: '#666',
            marginBottom: 6 
          }}>
            <Tag color="blue" size="small">
              {valueCount} giá trị
            </Tag>
          </div>

          {/* Sample values */}
          {attribute.values && attribute.values.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              {attribute.values.slice(0, 3).map(value => (
                <Tag 
                  key={value.id} 
                  size="small" 
                  style={{ 
                    margin: '1px 2px',
                    fontSize: 10
                  }}
                >
                  {value.value}
                </Tag>
              ))}
              {attribute.values.length > 3 && (
                <Tag size="small" color="blue" style={{ fontSize: 10 }}>
                  +{attribute.values.length - 3}
                </Tag>
              )}
            </div>
          )}

          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 8
          }}>
            <Space size={4}>
              <Button
                type="primary"
                size="small"
                icon={<EditOutlined />}
                onClick={() => navigate(`/attributes/edit/${attribute.id}`)}
              >
                Sửa
              </Button>
              <Button
                size="small"
                icon={<SettingOutlined />}
                onClick={() => navigate(`/attributes/${attribute.id}/values`)}
              >
                Giá trị
              </Button>
              <Popconfirm
                title="Xóa thuộc tính?"
                description="Sẽ xóa tất cả giá trị liên quan."
                onConfirm={() => handleDelete(attribute.id)}
                okText="Có"
                cancelText="Không"
                placement="topRight"
              >
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                />
              </Popconfirm>
            </Space>
          </div>
        </div>
      </Card>
    );
  };

  const handleDelete = async (id) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      message.error('Vui lòng đăng nhập lại');
      return;
    }

    try {
      const response = await attributeService.delete(id);
      if (response.success) {
        message.success('Xóa thuộc tính thành công');
        loadAttributes();
      } else {
        message.error(response.message || 'Xóa thuộc tính thất bại');
      }
    } catch (error) {
      //console.error('Error deleting attribute:', error);
      const errorMsg = error.response?.data?.message || 'Lỗi khi xóa thuộc tính';
      message.error(errorMsg);
    }
  };

  // Filter attributes based on search
  const filteredAttributes = attributes.filter(attr =>
    attr.name?.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: 'Tên thuộc tính',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{text}</div>
          {record.values && record.values.length > 0 && (
            <div style={{ marginTop: 4 }}>
              {record.values.slice(0, 3).map(value => (
                <Tag key={value.id} size="small" style={{ margin: '2px' }}>
                  {value.value}
                </Tag>
              ))}
              {record.values.length > 3 && (
                <Tag size="small" color="blue">+{record.values.length - 3}</Tag>
              )}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Số giá trị',
      key: 'valueCount',
      width: 100,
      render: (_, record) => (
        <div style={{ textAlign: 'center' }}>
          {record.values ? record.values.length : 0}
        </div>
      ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => navigate(`/attributes/edit/${record.id}`)}
          >
            Sửa
          </Button>
          <Button
            type="default"
            icon={<SettingOutlined />}
            size="small"
            onClick={() => navigate(`/attributes/${record.id}/values`)}
          >
            Giá trị
          </Button>
          <Popconfirm
            title="Bạn có chắc chắn muốn xóa thuộc tính này?"
            description="Hành động này sẽ xóa tất cả giá trị thuộc tính liên quan."
            onConfirm={() => handleDelete(record.id)}
            okText="Có"
            cancelText="Không"
          >
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              Xóa
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        {/* Header và bộ lọc */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="Tìm kiếm thuộc tính..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={16} style={{ textAlign: 'right' }}>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadAttributes}
                loading={loading}
              >
                Làm mới
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/attributes/add')}
              >
                Thêm thuộc tính
              </Button>
            </Space>
          </Col>
        </Row>

        {/* Thống kê */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={8}>
            <div style={{ 
              background: '#f0f9ff', 
              padding: 16, 
              borderRadius: 8,
              textAlign: 'center' 
            }}>
              <div style={{ fontSize: 24, fontWeight: 'bold', color: '#1890ff' }}>
                {filteredAttributes.length}
              </div>
              <div style={{ color: '#666' }}>Tổng thuộc tính</div>
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div style={{ 
              background: '#f6ffed', 
              padding: 16, 
              borderRadius: 8,
              textAlign: 'center' 
            }}>
              <div style={{ fontSize: 24, fontWeight: 'bold', color: '#52c41a' }}>
                {filteredAttributes.reduce((sum, attr) => sum + (attr.values ? attr.values.length : 0), 0)}
              </div>
              <div style={{ color: '#666' }}>Tổng giá trị</div>
            </div>
          </Col>
        </Row>

        {/* Responsive Attribute Display */}
        {isMobile ? (
          // Mobile: Card Layout
          <div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: 16,
              padding: '8px 0',
              borderBottom: '1px solid #f0f0f0'
            }}>
              <div style={{ fontSize: 14, color: '#666' }}>
                Hiển thị {filteredAttributes.length} thuộc tính
              </div>
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={loadAttributes}
                loading={loading}
              >
                Tải lại
              </Button>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <div>Đang tải...</div>
              </div>
            ) : filteredAttributes.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: 40,
                color: '#999'
              }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>🏷️</div>
                <div>Không có thuộc tính nào</div>
              </div>
            ) : (
              filteredAttributes.map(attribute => (
                <MobileAttributeCard key={attribute.id} attribute={attribute} />
              ))
            )}
          </div>
        ) : (
          // Desktop: Table Layout
          <Table
            columns={columns}
            dataSource={filteredAttributes}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} của ${total} thuộc tính`,
            }}
            scroll={{ x: 800 }}
          />
        )}
      </Card>
    </div>
  );
};

export default AttributeList;
